import { Button } from "@aurumiq/ui/components/button";
import { type ColumnDef, createColumnHelper, DataTable } from "@aurumiq/ui/composite/data-table";
import { ReasonDialog } from "@aurumiq/ui/composite/reason-dialog";
import { StatusTag, statusVariant } from "@aurumiq/ui/composite/status-tag";
import { dmy, relativeTime } from "@aurumiq/ui/lib/format";
import { AlertTriangle, Apple, Ban, Monitor, Smartphone } from "lucide-react";
import { useMemo, useState } from "react";
import { usePermission } from "@/auth/use-permission";
import { errorMessage } from "@/lib/api";
import type { Device } from "@/lib/types";
import { useOrgDevices, useRevokeDevice } from "./queries";

const col = createColumnHelper<Device>();
const DRIFT_LIMIT_S = 3600;

export function PlatformLabel({ platform }: { platform: string }) {
  const p = platform.toLowerCase();
  const Icon =
    p.includes("mac") || p.includes("ios") || p.includes("darwin")
      ? Apple
      : p.includes("android") || p.includes("phone")
        ? Smartphone
        : Monitor;
  return (
    <span className="inline-flex items-center gap-1.5">
      <Icon className="size-4 text-muted-foreground" aria-hidden="true" />
      {platform}
    </span>
  );
}

export function DevicesTab({ orgId }: { orgId: string }) {
  const canWrite = usePermission("devices:write");
  const devices = useOrgDevices(orgId);
  const revoke = useRevokeDevice(orgId);
  const [revoking, setRevoking] = useState<Device | null>(null);

  const columns = useMemo(
    () => [
      col.accessor("name", {
        header: "Device",
        cell: (c) => (
          <div>
            <div className="font-semibold">{c.getValue()}</div>
            <div className="font-mono text-muted-foreground text-xs">{c.row.original.id}</div>
          </div>
        ),
      }),
      col.accessor("platform", {
        header: "Platform",
        cell: (c) => <PlatformLabel platform={c.getValue()} />,
      }),
      col.accessor("app_version", {
        header: "Versions",
        cell: (c) => (
          <div>
            <div>app {c.getValue() ?? "—"}</div>
            <div className="text-muted-foreground text-xs">{c.row.original.os_version ?? ""}</div>
          </div>
        ),
      }),
      col.accessor("status", {
        header: "Status",
        cell: (c) => (
          <StatusTag variant={statusVariant(c.getValue())}>{c.getValue().toUpperCase()}</StatusTag>
        ),
      }),
      col.accessor("activated_at", { header: "Activated", cell: (c) => dmy(c.getValue()) || "—" }),
      col.accessor("last_seen_at", {
        header: "Last seen",
        cell: (c) => <span title={c.getValue() ?? undefined}>{relativeTime(c.getValue())}</span>,
      }),
      col.accessor("clock_drift_s", {
        header: "Clock drift",
        meta: { numeric: true },
        cell: (c) => {
          const v = c.getValue();
          if (v === null || v === undefined) return "—";
          const bad = Math.abs(v) > DRIFT_LIMIT_S;
          return (
            <span
              className={
                bad ? "inline-flex items-center gap-1 font-semibold text-warning" : undefined
              }
            >
              {bad ? (
                <AlertTriangle className="size-3.5" aria-label="Clock drift exceeds one hour" />
              ) : null}
              {v > 0 ? "+" : ""}
              {v} s
            </span>
          );
        },
      }),
      col.display({
        id: "actions",
        header: "",
        meta: { numeric: true },
        cell: (c) =>
          canWrite && c.row.original.status !== "revoked" ? (
            <Button
              variant="ghost"
              size="sm"
              className="text-danger"
              onClick={() => setRevoking(c.row.original)}
              aria-label={`Revoke ${c.row.original.name}`}
            >
              <Ban /> Revoke
            </Button>
          ) : null,
      }),
    ],
    [canWrite],
  );

  return (
    <>
      <DataTable
        columns={columns as ColumnDef<Device, unknown>[]}
        data={devices.data?.items ?? []}
        isLoading={devices.isPending}
        getRowId={(d) => d.id}
        emptyMessage={
          devices.isError
            ? `Could not load devices: ${errorMessage(devices.error)}`
            : "No devices activated yet."
        }
      />
      <ReasonDialog
        open={revoking !== null}
        onOpenChange={(o) => {
          if (!o) setRevoking(null);
        }}
        title={`Revoke ${revoking?.name}?`}
        description="Revokes the device's licence and frees its seat. The device must re-activate to continue."
        confirmLabel="Revoke device"
        onConfirm={async (reason) => {
          if (revoking) await revoke.mutateAsync({ did: revoking.id, reason });
        }}
      />
    </>
  );
}
